import React from 'react';
import { Box, Grid, Paper, TableContainer } from '@mui/material';
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TablePagination from '@mui/material/TablePagination';
import TableRow from "@mui/material/TableRow";
import Fuse from 'fuse.js';
import { getComparatorSort } from '../../helpers';
import { LIST_NULL_OPTION_VALUE, PLAYLIST_FILTERS_OPTIONS } from '../../helpers/constant';
import SearchBar from '../search/searchbar';
import TableFilter from './filters';
import TableHeader from './header';
import TablePaginationActions from './pagination-actions';
import TableLoader from './table-loader';

const TableComponent = ({ spinner, columns, data, noDataMessage, rowClick, rowStyle, defaultOptions, pagination, searchKeys = [], filterTypes = {}, defaultFilterValues = {}, companyListOptions = [], headerActionButtons }) => {
    const [sortKey, setSortKey] = React.useState(defaultOptions?.sortKey || "");
    const [order, setOrder] = React.useState(defaultOptions?.sortOrder || "");
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(defaultOptions?.rowPerPage || 10);
    const [searchValue, setSearchValue] = React.useState("");
    const [visibleRows, setVisibleRows] = React.useState([...data]);
    const [filterValues, setFilterValues] = React.useState(defaultFilterValues); // Initialize with default filter values
    const previousFilterValues = React.useRef(defaultFilterValues);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleSort = (property, newOrder) => {
        const isAsc = newOrder === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setSortKey(property);
        setPage(0);
    };

    const sortedRows = React.useMemo(() => {
        if (searchKeys.length === 0) { // More clear
            return [...data];
        }
        return [...data].sort(getComparatorSort(order, sortKey));
    }, [order, sortKey, data, searchKeys.length]);

    const fuse = React.useMemo(() => {
        return new Fuse(sortedRows, {
            keys: searchKeys || [],
            threshold: 0.2,
        });
    }, [sortedRows, searchKeys]);

    React.useEffect(() => {
        if (sortedRows?.length === 0) {
            return;
        }
        const list = searchValue.trim() ? fuse.search(searchValue).map(result => result.item) : sortedRows;
        // Only reset page if the current page is out of range
        if (page * rowsPerPage >= sortedRows.length || list.length === 0) {
            setPage(0);
        }
        setVisibleRows([...list]);
    }, [searchValue, sortedRows]);

    // update filterValues
    const handleFilters = (values) => {
        let hasChanged = false;
        for (const key in values) {
            if (previousFilterValues.current[key] !== values[key]) {
                hasChanged = true;
                break;
            }
        }
        if (hasChanged) {
            previousFilterValues.current = values;
            setFilterValues(values);
        }
    };

    const filteredRows = React.useMemo(() => {
        return visibleRows.filter((row) => {
            for (const key in filterTypes) {
                // custom code written for playlist
                if (key === "playListBookmark" && filterValues[key] !== undefined && filterValues[key] !== PLAYLIST_FILTERS_OPTIONS[0] && !row[filterTypes[key]].includes(filterValues[key])) {
                    return false;
                }
                if (key !== "playListBookmark" && filterValues[key] !== undefined && filterValues[key] !== LIST_NULL_OPTION_VALUE && row[filterTypes[key]] !== filterValues[key]) {
                    return false;
                }
            }
            return true;
        });
    }, [visibleRows, filterValues, filterTypes]);

    const getListData = React.useCallback(() => {
        if (!pagination) return filteredRows;
        return filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [filteredRows, page, rowsPerPage, pagination]);

    return (
        <Grid
            className="vocalysd-list-table"
            display="flex"
            flexDirection="column"
            gap={2}
            height="100%"
        >
            <Box
                display="flex"
                flexWrap="wrap"
                flexDirection="row"
                justifyContent="space-between"
                gap={2}
            >
                <Box
                    display="flex"
                    flexWrap="wrap"
                    flexDirection="row"
                    justifyContent="space-between"
                    gap={2}
                >
                    {searchKeys?.length ?
                        <SearchBar
                            callBack={(searchValue) => setSearchValue(searchValue)}
                        /> : ""}
                    {Object.keys(filterTypes).length ? <TableFilter
                        spinner={spinner}
                        filterTypes={filterTypes}
                        defaultFilterValues={defaultFilterValues}
                        companyListOptions={companyListOptions}
                        onChange={handleFilters}
                    /> : ""}
                </Box>
                {headerActionButtons?.()}
            </Box>
            <Paper
                sx={{display: "flex", flexDirection: "column", flexGrow: 1, width: '100%', overflow: 'hidden', borderRadius: 2.5 }}
            >
                <TableContainer sx={{ maxHeight: "100%", flexGrow: 1 }}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHeader
                            columns={columns}
                            order={order}
                            orderBy={sortKey}
                            onRequestSort={handleSort}
                        />
                        <TableBody>
                            {spinner ? (
                                <TableLoader columnsLength={columns.length} />
                            ) : (
                                getListData().map((item, index) => (
                                    <TableRow hover key={index} sx={{
                                        cursor: rowClick ? "pointer" : "initial",
                                        ...rowStyle
                                    }} onClick={() => rowClick ? rowClick(item) : {}}>
                                        {columns.map((c, indexColumn) => (
                                            <TableCell key={index + "-" + indexColumn} onClick={item.handleClick} align={c.align} sx={c.cellStyle}
                                                className={c.cellClassName ? c.cellClassName(item) : ""}>
                                                {c.renderItem(item, index, indexColumn)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )}
                            {!spinner && !filteredRows?.length ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length}>
                                        {noDataMessage}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                ""
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {pagination ? <TablePagination
                    rowsPerPageOptions={[10, 25, 100]}
                    component="div"
                    count={filteredRows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    disabled={spinner}
                    ActionsComponent={TablePaginationActions}
                    sx={{
                        '& .MuiToolbar-root': {
                            flexWrap: 'wrap',
                            px: 2,
                            py: 1,
                            justifyContent: {
                                xs: 'center',
                                sm: 'end',
                            },
                        },
                    }}
                /> : ""}
            </Paper>
        </Grid>
    );
};

export default React.memo(TableComponent);